import { useState, useEffect, useCallback, useMemo } from 'react';
import { genericErrorAlert } from '../utils/alertUtils';
import { captureException } from '../utils/sentryUtils';
import { useGetLatestApprovalQuery } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from '../constants/approvalTypeIds';
import { useAppHubUsers } from './useAppHubUsers';
import { useBreezeAgents } from './useBreezeAgents';
import { chirpApprovalToApprovalData, hasPendingAgentApprovalStep } from '../utils/chirpApprovalAdapters';
import { ACTOR } from '../constants/actors';
import { useAgentApprovalPolling } from './useAgentApprovalPolling';
import { useCreateApproval } from './useCreateApproval';
import { useApproveApproval } from './useApproveApproval';
import { useRejectApproval } from './useRejectApproval';
import { useCancelApproval } from './useCancelApproval';
import { useUpdateApproval } from './useUpdateApproval';
import { useRequestChangesOnApproval } from './useRequestChangesOnApproval';
import { useReRequestReview } from './useReRequestReview';
export const useApprovalData = (approvalType, approvalAction, approvableObjectId, assetName, approvalObjectIds) => {
  const [fetchError, setFetchError] = useState();
  const [hasPendingAgentStep, setHasPendingAgentStep] = useState(false);
  const pollInterval = useAgentApprovalPolling({
    hasPendingAgentStep,
    approvableObjectId
  });
  const {
    data: chirpData,
    loading: chirpLoading,
    refetch: chirpRefetch
  } = useGetLatestApprovalQuery({
    variables: {
      approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
      approvalAction,
      approvableObjectId: approvableObjectId
    },
    skip: !approvableObjectId,
    /**
     * fetchPolicy 'network-only' ensures fresh data.
     * We cannot remove this as we always need to fetch the latest approval.
     */
    fetchPolicy: 'network-only',
    pollInterval,
    onError: chirpError => {
      var _networkError;
      const errorType = (chirpError === null || chirpError === void 0 || (_networkError = chirpError.networkError) === null || _networkError === void 0 || (_networkError = _networkError.cause) === null || _networkError === void 0 ? void 0 : _networkError.errorType) || 'UNKNOWN_SUBCATEGORY';
      setFetchError(errorType);
      genericErrorAlert();
      captureException(chirpError, {
        extra: {
          fingerprint: 'useApprovalData-fetchLatest-chirp',
          approvalType,
          approvalAction,
          approvableObjectId
        }
      });
    }
  });
  const actorUserIds = useMemo(() => {
    const chirpApproval = chirpData === null || chirpData === void 0 ? void 0 : chirpData.approval;
    if (!chirpApproval) return [];
    const ids = [];
    if (chirpApproval.requester.actorType === ACTOR.USER) {
      ids.push(Number(chirpApproval.requester.id));
    }
    for (const step of (_chirpApproval$approv = chirpApproval.approvalSteps) !== null && _chirpApproval$approv !== void 0 ? _chirpApproval$approv : []) {
      var _chirpApproval$approv;
      if (step.approver.actorType === ACTOR.USER) {
        ids.push(Number(step.approver.id));
      }
    }
    return [...new Set(ids)];
  }, [chirpData]);
  const actorAgentIds = useMemo(() => {
    const chirpApproval = chirpData === null || chirpData === void 0 ? void 0 : chirpData.approval;
    if (!chirpApproval) return [];
    const ids = [];
    if (chirpApproval.requester.actorType === ACTOR.AGENT) {
      ids.push(chirpApproval.requester.id);
    }
    for (const step of (_chirpApproval$approv2 = chirpApproval.approvalSteps) !== null && _chirpApproval$approv2 !== void 0 ? _chirpApproval$approv2 : []) {
      var _chirpApproval$approv2;
      if (step.approver.actorType === ACTOR.AGENT) {
        ids.push(step.approver.id);
      }
    }
    return [...new Set(ids)];
  }, [chirpData]);
  useEffect(() => {
    setHasPendingAgentStep(hasPendingAgentApprovalStep(chirpData === null || chirpData === void 0 ? void 0 : chirpData.approval));
  }, [chirpData]);
  const {
    usersRecord: approverUsers,
    loading: approverUsersLoading,
    error: fetchApproverUsersError
  } = useAppHubUsers({
    userIds: actorUserIds
  });
  const {
    agentsRecord: approverAgents,
    loading: approverAgentsLoading
  } = useBreezeAgents({
    agentIds: actorAgentIds
  });
  const resolvedData = useMemo(() => {
    const chirpApproval = chirpData === null || chirpData === void 0 ? void 0 : chirpData.approval;
    if (!chirpApproval || approverUsersLoading || approverAgentsLoading) return null;
    return chirpApprovalToApprovalData(chirpApproval, approverUsers, approverAgents, approvalType, approvalAction);
  }, [chirpData, approverUsers, approverUsersLoading, approverAgents, approverAgentsLoading, approvalType, approvalAction]);
  const fetchLatest = useCallback(() => {
    void chirpRefetch();
  }, [chirpRefetch]);
  const create = useCreateApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    assetName,
    approvalObjectIds,
    usersRecord: approverUsers,
    agentsRecord: approverAgents
  });
  const approve = useApproveApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    resolvedData
  });
  const reject = useRejectApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    resolvedData
  });
  const cancel = useCancelApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    resolvedData
  });
  const update = useUpdateApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    resolvedData
  });
  const requestChanges = useRequestChangesOnApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    resolvedData
  });
  const reRequestReview = useReRequestReview({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    resolvedData
  });
  const api = useMemo(() => ({
    fetchLatest,
    create,
    cancel,
    reject,
    approve,
    update,
    requestChanges,
    reRequestReview
  }), [fetchLatest, create, cancel, reject, approve, update, requestChanges, reRequestReview]);
  return {
    data: resolvedData,
    isLoading: chirpLoading || approverUsersLoading || approverAgentsLoading,
    api,
    fetchError,
    hasFetchApproverUsersError: Boolean(fetchApproverUsersError)
  };
};